import { Link } from "react-router-dom";
import React, { useState } from "react";
import { base44 } from "../api/base44Client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Mail, Phone, MapPin, Send, CheckCircle, Facebook, Instagram, Linkedin, Clock, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function Contact() {
  const [formData, setFormData] = useState({ name:'', email:'', phone:'', subject:'', message:'' });
  const [success, setSuccess] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => { try { const all = await base44.entities.Settings.list(); return all.length > 0 ? all[0] : null; } catch { return null; } },
    retry: false, staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: true
  });

  const s = settings || { address:"Abidjan, Côte d'Ivoire", phone:'+225 0585868767', email:'contact@r2bentreprises.com', hours_monday_friday:'8h00 - 18h00', hours_saturday:'9h00 - 14h00', hours_sunday:'Fermé', facebook_url:'', instagram_url:'', linkedin_url:'' };

  const sendMessageMutation = useMutation({
    mutationFn: async (messageData) => base44.entities.ContactMessage.create(messageData),
    onSuccess: () => {
      setSuccess(true);
      setFormData({ name:'', email:'', phone:'', subject:'', message:'' });
      setTimeout(() => setSuccess(false), 5000);
    }
  });

  const handleSubmit = (e) => { e.preventDefault(); sendMessageMutation.mutate(formData); };

  const contactInfo = [
    { icon:MapPin, title:"Adresse", content:s.address, color:"from-blue-500 to-blue-600" },
    { icon:Phone, title:"Téléphone", content:s.phone, color:"from-green-500 to-green-600" },
    { icon:Mail, title:"Email", content:s.email, color:"from-purple-500 to-purple-600" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} className="text-center">
            <Mail className="w-16 h-16 mx-auto mb-4 text-[#c41e3a]" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contactez-Nous</h1>
            <p className="text-xl text-gray-200">Nous sommes à votre écoute pour répondre à toutes vos questions</p>
          </motion.div>
        </div>
      </section>

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="bg-green-50 border border-green-200 flex items-center gap-2 p-4 rounded-lg"><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-green-800">Votre message a été envoyé avec succès ! Nous vous répondrons bientôt.</span></div>
        </div>
      )}

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {contactInfo.map((info, index) => (
              <motion.div key={index} initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }}>
                <div className="border-none shadow-lg hover:shadow-xl transition-shadow text-center p-8 rounded-xl bg-white">
                  <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${info.color} rounded-full flex items-center justify-center`}><info.icon className="w-8 h-8 text-white" /></div>
                  <h3 className="text-xl font-bold text-black mb-2">{info.title}</h3>
                  <p className="text-gray-600">{info.content}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div initial={{ opacity:0,x:-30 }} animate={{ opacity:1,x:0 }}>
              <div className="border-none shadow-xl h-full p-8 rounded-xl bg-white">
                <h2 className="text-2xl font-bold text-black mb-6">Envoyez-nous un message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {[{label:"Nom complet *",id:"name",type:"text",placeholder:"Votre nom complet"},{label:"Email *",id:"email",type:"email",placeholder:"votre@email.com"},{label:"Téléphone",id:"phone",type:"tel",placeholder:"+225 0585868767"},{label:"Sujet *",id:"subject",type:"text",placeholder:"Sujet de votre message"}].map(f => (
                    <div key={f.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                      <input type={f.type} required={f.label.includes('*')} value={formData[f.id]} onChange={e => setFormData({...formData,[f.id]:e.target.value})} placeholder={f.placeholder}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
                    </div>
                  ))}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                    <textarea required rows={6} value={formData.message} onChange={e => setFormData({...formData,message:e.target.value})} placeholder="Votre message..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
                  </div>
                  <button type="submit" disabled={sendMessageMutation.isPending} className="w-full accent-gradient text-white py-4 text-lg rounded-lg hover:opacity-90 flex items-center justify-center gap-2">
                    {sendMessageMutation.isPending ? 'Envoi en cours...' : <><Send className="w-5 h-5" />Envoyer le message</>}
                  </button>
                </form>
              </div>
            </motion.div>

            <motion.div initial={{ opacity:0,x:30 }} animate={{ opacity:1,x:0 }} className="space-y-6">
              <div className="border-none shadow-xl p-8 rounded-xl bg-white">
                <h3 className="text-2xl font-bold text-black mb-4 flex items-center gap-2"><Clock className="w-6 h-6" />Horaires d'ouverture</h3>
                <div className="space-y-3 text-gray-700">
                  {[{day:"Lundi - Vendredi",hours:s.hours_monday_friday},{day:"Samedi",hours:s.hours_saturday},{day:"Dimanche",hours:s.hours_sunday,red:true}].map((h,i) => (
                    <div key={i} className="flex justify-between"><span className="font-medium">{h.day}</span><span className={h.red ? 'text-red-600' : ''}>{h.hours}</span></div>
                  ))}
                </div>
              </div>

              <div className="border-none shadow-xl gradient-bg text-white p-8 rounded-xl">
                <h3 className="text-2xl font-bold mb-4">Suivez-nous</h3>
                <p className="text-gray-200 mb-6">Restez informé de nos dernières actualités sur nos réseaux sociaux</p>
                <div className="flex gap-4">
                  {s.facebook_url && <a href={s.facebook_url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/20 hover:bg-[#c41e3a] rounded-full flex items-center justify-center transition-colors"><Facebook className="w-6 h-6" /></a>}
                  {s.instagram_url && <a href={s.instagram_url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/20 hover:bg-[#c41e3a] rounded-full flex items-center justify-center transition-colors"><Instagram className="w-6 h-6" /></a>}
                  {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/20 hover:bg-[#c41e3a] rounded-full flex items-center justify-center transition-colors"><Linkedin className="w-6 h-6" /></a>}
                </div>
              </div>

              <div className="border-none shadow-xl bg-gradient-to-br from-[#c41e3a] to-[#a61c3c] p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-white mb-4">Besoin d'aide immédiate ?</h3>
                <p className="text-white mb-6">Notre équipe d'experts est disponible pour répondre à toutes vos questions</p>
                <div className="space-y-3">
                  {settings?.whatsapp_number && <a href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer"><button className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 rounded-lg flex items-center justify-center gap-2"><MessageCircle className="w-4 h-4" />WhatsApp</button></a>}
                  <a href={`tel:${s.phone}`}><button className="w-full bg-black hover:bg-gray-900 text-white py-3 rounded-lg flex items-center justify-center gap-2"><Phone className="w-4 h-4" />{s.phone}</button></a>
                  <a href={`mailto:${s.email}`}><button className="w-full border-2 border-white text-white bg-transparent hover:bg-white hover:text-[#c41e3a] py-3 rounded-lg flex items-center justify-center gap-2"><Mail className="w-4 h-4" />{s.email}</button></a>
                </div>
              </div>

              {settings?.google_maps_embed_url && (
                <div className="overflow-hidden shadow-xl rounded-xl">
                  <iframe src={settings.google_maps_embed_url} width="100%" height="300" style={{border:0}} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Localisation R2B Entreprises"></iframe>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
